import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n";
import { services, IMAGES } from "../data/services";
import { Seo, localBusinessJsonLd } from "../components/site/Seo";
import { MaskLine, FadeUp } from "../components/site/Reveal";
import { Ribbon } from "../components/site/Ribbon";
import { Testimonial } from "../components/site/Testimonial";
import { CTA } from "../components/site/CTA";
import * as Icons from "lucide-react";
import { ArrowUpRight, ArrowDown, ShieldCheck, Zap, FileCheck2, MapPin } from "lucide-react";

const T = {
  fr: {
    tag: "Smart Aircraft — Luxembourg",
    l1: "L'expertise", l2: "vue du ciel.",
    sub: "Fixedrone transforme chaque vol en données exploitables : inspection, thermographie, cartographie 3D et agriculture de précision. Au service des entreprises et collectivités du Luxembourg et des régions frontalières.",
    cta: "Demander un devis", cta2: "Découvrir nos services", scroll: "Défiler",
    chips: ["Inspection toiture", "Thermographie", "Photovoltaïque", "Orthophoto", "Modèle 3D", "Cubature", "Relevés RTK", "Rapports techniques"],
    whyTag: "Pourquoi Fixedrone",
    whyTitle: "Une donnée précise, pas seulement de belles images.",
    whyLead: "Nous livrons des mesures fiables et des rapports exploitables. Concrètement, voici ce que vous obtenez.",
    why: [
      { i: ShieldCheck, t: "Pilotes certifiés & assurés", d: "Interventions sécurisées, conformes à la réglementation aérienne européenne." },
      { i: Zap, t: "Précision centimétrique (RTK)", d: "Photogrammétrie et GPS RTK pour des mesures fiables et géoréférencées." },
      { i: FileCheck2, t: "Rapports techniques clairs", d: "Livrables annotés et exploitables dans vos logiciels CAO / BIM / SIG." },
      { i: MapPin, t: "Proximité transfrontalière", d: "Luxembourg, France, Belgique et Allemagne — réactivité et déplacements courts." },
    ],
    servTag: "Nos services", servTitle: "Sept expertises, une vision du ciel.",
    servLead: "Chaque mission est cadrée selon votre besoin. Découvrez le service adapté à votre secteur.",
    seeAll: "Tous les services", learn: "En savoir plus",
    statLabels: ["Zones couvertes", "Précision RTK", "Réponse devis", "Sans échafaudage"],
    stats: ["4 pays", "±1 cm", "24-48 h", "100 %"],
  },
  en: {
    tag: "Smart Aircraft — Luxembourg",
    l1: "Expertise", l2: "from the sky.",
    sub: "Fixedrone turns every flight into actionable data: inspection, thermography, 3D mapping and precision agriculture. Serving businesses and communities in Luxembourg and border regions.",
    cta: "Request a quote", cta2: "Explore our services", scroll: "Scroll",
    chips: ["Roof inspection", "Thermography", "Photovoltaic", "Orthophoto", "3D model", "Volumetrics", "RTK survey", "Technical reports"],
    whyTag: "Why Fixedrone",
    whyTitle: "Precise data, not just beautiful images.",
    whyLead: "We deliver reliable measurements and actionable reports. Here is concretely what you get.",
    why: [
      { i: ShieldCheck, t: "Certified & insured pilots", d: "Safe operations, compliant with European aviation regulations." },
      { i: Zap, t: "Centimeter accuracy (RTK)", d: "Photogrammetry and RTK GPS for reliable georeferenced measurements." },
      { i: FileCheck2, t: "Clear technical reports", d: "Annotated deliverables usable in your CAD / BIM / GIS software." },
      { i: MapPin, t: "Cross-border proximity", d: "Luxembourg, France, Belgium and Germany — fast, short trips." },
    ],
    servTag: "Our services", servTitle: "Seven expertises, one view from the sky.",
    servLead: "Every mission is scoped to your need. Find the service that fits your sector.",
    seeAll: "All services", learn: "Learn more",
    statLabels: ["Countries served", "RTK accuracy", "Quote response", "No scaffolding"],
    stats: ["4 countries", "±1 cm", "24-48 h", "100 %"],
  },
};

const Hero = () => {
  const { lang } = useLang();
  const t = T[lang];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-[#050505]">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.heroCity} alt="Vue aérienne nocturne — Fixedrone drone Luxembourg" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 z-0 bg-black/60" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
      <div className="absolute inset-0 z-0 grid-lines opacity-40" />

      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16 md:pb-20 pt-40">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }} className="flex items-center gap-3 mb-7">
          <span className="w-8 h-px bg-[var(--primary)]" />
          <span className="font-mono-p text-xs md:text-sm uppercase tracking-[0.25em] text-primary-c">{t.tag}</span>
        </motion.div>
        <h1 className="font-display font-black leading-[0.85] tracking-tighter text-[15vw] md:text-[12vw] lg:text-[10vw]">
          <MaskLine delay={0.5} load>{t.l1}</MaskLine>
          <MaskLine delay={0.65} load className="text-primary-c italic">{t.l2}</MaskLine>
        </h1>
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9 }} className="max-w-xl text-base md:text-lg leading-relaxed text-white/70">{t.sub}</motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }} className="flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-cta" className="group inline-flex items-center gap-2 bg-[var(--primary)] text-black font-medium px-7 py-4 rounded-full hover:bg-white transition-colors duration-300">
              {t.cta} <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <Link to="/services" data-testid="hero-cta-2" className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 rounded-full hover:border-[var(--primary)] hover:text-primary-c transition-colors duration-300">{t.cta2}</Link>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.9 }} className="mt-10 flex flex-wrap gap-2">
          {t.chips.map((c) => (
            <span key={c} className="font-mono-p text-xs uppercase tracking-wider text-white/70 border border-white/15 rounded-full px-4 py-2 backdrop-blur-sm bg-white/[0.03]">{c}</span>
          ))}
        </motion.div>
      </div>
      <div className="absolute bottom-8 right-6 md:right-16 flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-white/50">{t.scroll} <ArrowDown size={14} /></div>
    </section>
  );
};

const ServiceCard = ({ s, lang, learn, index }) => {
  const Icon = Icons[s.icon] || Icons.Plane;
  return (
    <FadeUp delay={(index % 3) * 0.06}>
      <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`} className="group relative block overflow-hidden clip-frame border border-white/10 h-full min-h-[320px] bg-[#0c0c0d]">
        <img src={s.hero} alt={s[lang].name} className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-[1s]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="relative p-7 h-full flex flex-col justify-between min-h-[320px]">
          <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-primary-c group-hover:bg-[var(--primary)] group-hover:text-black transition-colors duration-300">
            <Icon size={20} />
          </span>
          <div>
            <h3 className="font-display font-semibold text-2xl md:text-[26px] tracking-tight leading-tight">{s[lang].name}</h3>
            <p className="text-white/60 text-sm mt-2 leading-relaxed">{s[lang].tagline}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 font-mono-p text-xs uppercase tracking-widest text-primary-c">
              {learn} <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform duration-300" />
            </span>
          </div>
        </div>
      </Link>
    </FadeUp>
  );
};

export default function Home() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <>
      <Seo
        title={T.fr.tag && (lang === "fr"
          ? "Fixedrone | Drone pro : inspection, thermographie & cartographie 3D au Luxembourg"
          : "Fixedrone | Pro drone: inspection, thermography & 3D mapping in Luxembourg")}
        description={lang === "fr"
          ? "Services de drone professionnels au Luxembourg et régions frontalières (France, Belgique, Allemagne) : inspection de toiture, thermographie infrarouge, photovoltaïque, cartographie, topographie 3D, cubature RTK et agriculture de précision."
          : "Professional drone services in Luxembourg and border regions: roof inspection, infrared thermography, photovoltaic, mapping, 3D surveying, RTK volumetrics and precision agriculture."}
        keywords="drone Luxembourg, inspection toiture drone, thermographie drone, inspection photovoltaïque, cartographie drone, topographie drone, orthophoto, modèle 3D, cubature, relevés RTK, agriculture de précision, drone France Belgique Allemagne"
        path="/"
        image={IMAGES.heroCity}
        jsonLd={localBusinessJsonLd}
      />
      <Hero />

      {/* Stats bar */}
      <section className="bg-[#070708] border-b border-white/10">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {t.stats.map((v, i) => (
            <div key={i} className="py-8 md:py-10 px-4 text-center md:text-left">
              <div className="font-display font-black text-3xl md:text-4xl text-primary-c">{v}</div>
              <div className="font-mono-p text-xs uppercase tracking-widest text-white/40 mt-2">{t.statLabels[i]}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{t.whyTag}</span></FadeUp>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-6"><MaskLine>{t.whyTitle}</MaskLine></h2>
            <FadeUp delay={0.1}><p className="text-white/60 mt-6 text-base md:text-lg leading-relaxed">{t.whyLead}</p></FadeUp>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {t.why.map((w, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="bg-[#0a0a0c] p-8 h-full min-h-[240px] flex flex-col hover:bg-[#0f0f12] transition-colors duration-500">
                  <w.i className="text-primary-c" size={26} />
                  <h3 className="font-display font-semibold text-xl mt-6">{w.t}</h3>
                  <p className="text-white/55 text-sm mt-3 leading-relaxed">{w.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Ribbon />

      {/* Services grid */}
      <section className="py-24 md:py-32 bg-[#070708]">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{t.servTag}</span></FadeUp>
              <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-6"><MaskLine>{t.servTitle}</MaskLine></h2>
              <FadeUp delay={0.1}><p className="text-white/60 mt-5 leading-relaxed">{t.servLead}</p></FadeUp>
            </div>
            <Link to="/services" className="shrink-0 inline-flex items-center gap-2 font-mono-p text-sm border border-white/20 px-6 py-3 rounded-full hover:border-[var(--primary)] hover:text-primary-c transition-colors">{t.seeAll} <ArrowUpRight size={15} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (<ServiceCard key={s.slug} s={s} lang={lang} learn={t.learn} index={i} />))}
          </div>
        </div>
      </section>

      <Testimonial />
      <CTA />
    </>
  );
}
